import { useLayoutEffect } from 'react';

// (30 Oct 2026, decision 160) The top buttons on phones (below Tailwind's sm, 640 px):
// one row when they all fit; otherwise the fewest rows that fit, split as evenly as
// possible by number (2 + 2, 3 + 3, the first row taking the extra), each row's buttons
// sharing its width equally. Measured on the fly, so it follows whichever buttons a
// calendar shows, the phone's width and the text size. The order stays as the order-N classes set it
// (decisions 133 and 135). Wider screens are untouched.
//
// containerRef: the flex container of the buttons. Inside it, elements marked
// data-row-break (e.g. <div data-row-break aria-hidden="true" className="hidden h-0" />)
// are the line breaks this places between rows: one for up to two rows, two for up to
// three (the dashboard, whose seven or more buttons may need three on a narrow phone).
// Should even that not fit, the buttons simply wrap at their own widths. A wrapper around
// a button (the Notifications bell) grows like the others, its button filling it.
// The styles are set on the elements directly (React sets no style on them), and
// removed again on wider screens, so the sm:order-none classes apply there as before.
const PHONE = '(max-width: 639px)';

export default function useBalancedRows(containerRef) {
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof window === 'undefined') return undefined;
    const mq = window.matchMedia ? window.matchMedia(PHONE) : null;
    let off = true;

    // The button inside a wrapper, if the item is not itself a button.
    const innerButton = (c) => (c.tagName === 'BUTTON' || c.tagName === 'A' ? null : c.querySelector('button'));
    const clear = (items, breaks) => {
      items.forEach((c) => {
        c.style.order = '';
        c.style.flex = '';
        c.style.justifyContent = '';
        const inner = innerButton(c);
        if (inner) { inner.style.width = ''; inner.style.justifyContent = ''; }
      });
      breaks.forEach((b) => { b.style.display = ''; b.style.order = ''; b.style.flexBasis = ''; });
    };

    const measure = () => {
      const breaks = Array.from(el.querySelectorAll(':scope > [data-row-break]'));
      const items = Array.from(el.children).filter((c) => !breaks.includes(c) && c.offsetParent !== null);
      if (!mq || !mq.matches || items.length === 0) {
        if (!off) { clear(items, breaks); off = true; }
        return;
      }
      off = false;
      // The visual order, from the order-N classes (inline orders removed first).
      items.forEach((c) => { c.style.order = ''; });
      const seq = items
        .map((c, i) => ({ c, i, order: Number(window.getComputedStyle(c).order) || 0 }))
        .sort((a, b) => a.order - b.order || a.i - b.i);
      // Natural widths: each button at its own width, for a moment, before the screen is drawn.
      items.forEach((c) => {
        c.style.flex = '0 0 auto';
        const inner = innerButton(c);
        if (inner) inner.style.width = '';
      });
      seq.forEach((s) => { s.w = s.c.getBoundingClientRect().width; });
      const gap = parseFloat(window.getComputedStyle(el).columnGap) || 0;
      const avail = el.clientWidth;
      const rowWidth = (part) => part.reduce((sum, s) => sum + s.w, 0) + gap * Math.max(0, part.length - 1);

      // The fewest rows (up to one more than there are break elements) that fit, split as
      // evenly as possible by number (earlier rows taking the extra), then by width.
      let plan = null; // the indexes where each new row starts
      const n = seq.length;
      for (let rows = 1; rows <= Math.min(n, breaks.length + 1) && !plan; rows += 1) {
        let best = null;
        const tryStarts = (starts) => {
          if (starts.length === rows - 1) {
            const bounds = [0, ...starts, n];
            const parts = [];
            for (let r = 0; r < rows; r += 1) parts.push(seq.slice(bounds[r], bounds[r + 1]));
            if (parts.some((p) => rowWidth(p) > avail + 0.5)) return;
            const counts = parts.map((p) => p.length);
            const widths = parts.map(rowWidth);
            const ideal = parts.map((_, r) => Math.floor(n / rows) + (r < n % rows ? 1 : 0));
            const countGap = counts.reduce((sum, c, r) => sum + Math.abs(c - ideal[r]), 0);
            const score = countGap * 100000 + (Math.max(...widths) - Math.min(...widths));
            if (!best || score < best.score) best = { starts: [...starts], score };
            return;
          }
          const from = starts.length ? starts[starts.length - 1] + 1 : 1;
          for (let k = from; k <= n - (rows - 1 - starts.length); k += 1) tryStarts([...starts, k]);
        };
        tryStarts([]);
        if (best) plan = best.starts;
      }

      seq.forEach((s, j) => {
        s.c.style.order = String(2 * j + 2);
        const grow = !!plan;
        s.c.style.flex = grow ? '1 1 0' : '0 0 auto';
        s.c.style.justifyContent = grow ? 'center' : '';
        const inner = innerButton(s.c);
        if (inner) { inner.style.width = grow ? '100%' : ''; inner.style.justifyContent = grow ? 'center' : ''; }
      });
      breaks.forEach((b, r) => {
        if (plan && r < plan.length) {
          b.style.display = 'block';
          b.style.flexBasis = '100%';
          b.style.order = String(2 * plan[r] + 1);
        } else {
          b.style.display = '';
          b.style.order = '';
          b.style.flexBasis = '';
        }
      });
    };

    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => measure()) : null;
    if (ro) ro.observe(el);
    const mo = typeof MutationObserver === 'function' ? new MutationObserver(() => measure()) : null;
    if (mo) mo.observe(el, { childList: true });
    const onMq = () => measure();
    if (mq && mq.addEventListener) mq.addEventListener('change', onMq);
    window.addEventListener('resize', onMq);
    return () => {
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onMq);
      window.removeEventListener('resize', onMq);
    };
  }, [containerRef]);
}
